import { AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';

import { protectFeature } from '~libs.shared.system';
import { visitPaths } from '~libs.desktop.shared.paths';

import { CalendarController } from './CalendarController';
import { StyledCalendarPanel } from './CalendarPanel.styled';
import { CalendarModeButtonGroup } from './SidePanel/CalendarModeButtonGroup';
import { CalendarSidePanel } from './SidePanel/CalendarSidePanel';
import { CalendarSidePanelPortal } from './SidePanel/CalendarSidePanel.portal';
import { HealthcareLocationTimetable } from './Timetable/HealthcareLocation/HealthcareLocationTimetable';
import { HealthcareLocationTimetableLoader } from './Timetable/HealthcareLocation/HealthcareLocationTimetable.loader';
import { SpecialistTimetable } from './Timetable/Specialist/SpecialistTimetable';
import { SpecialistTimetableLoader } from './Timetable/Specialist/SpecialistTimetable.loader';
import { CalendarActionBar } from './actionBar/CalendarActionBar';

type CalendarPanelProps = {
   readonly practitioners: AppointmentPractitioner[];
};

export const CalendarPanel = (props: CalendarPanelProps): JSX.Element => {
   const {
      bookPatientId,
      updateTermAppointmentId,
      restoreAppointmentId,
      waitlistEntryId,
      practitionerId,
   } = visitPaths.useCalendarSearchParams();
   const searchingAllDoctorsAvailable = protectFeature('searchingAllPractitioners');
   const morePractitioners = props.practitioners.length > 1;

   const forceSpecialistMode = Boolean(
      !searchingAllDoctorsAvailable ||
         bookPatientId ||
         updateTermAppointmentId ||
         restoreAppointmentId ||
         waitlistEntryId ||
         !morePractitioners
   );

   return (
      <CalendarActionBar waitlistEntryId={waitlistEntryId}>
         <CalendarController forceSpecialistMode={forceSpecialistMode}>
            {({ mode, currentDate, setCurrentDate, register }) => (
               <StyledCalendarPanel>
                  <CalendarSidePanel>
                     {!forceSpecialistMode && (
                        <CalendarSidePanelPortal placement={'selectors'}>
                           <CalendarModeButtonGroup register={register} />
                        </CalendarSidePanelPortal>
                     )}
                  </CalendarSidePanel>
                  {mode === 'specialist' && (
                     <SpecialistTimetableLoader>
                        {({ currentPractitioner, appointmentConfiguration }) => (
                           <SpecialistTimetable
                              selectedDate={currentDate}
                              setSelectedDate={setCurrentDate}
                              bookPatientId={bookPatientId}
                              restoreAppointmentId={restoreAppointmentId}
                              updateTermAppointmentId={updateTermAppointmentId}
                              waitlistEntryId={waitlistEntryId}
                              practitionerId={practitionerId}
                              currentPractitioner={currentPractitioner}
                              practitioners={props.practitioners}
                              appointmentConfiguration={appointmentConfiguration}
                           />
                        )}
                     </SpecialistTimetableLoader>
                  )}
                  {mode === 'healthcareLocation' && (
                     <HealthcareLocationTimetableLoader>
                        {({ appointmentConfiguration }) => (
                           <HealthcareLocationTimetable
                              register={register}
                              currentDate={currentDate}
                              setCurrentDate={setCurrentDate}
                              practitioners={props.practitioners}
                              appointmentConfiguration={appointmentConfiguration}
                           />
                        )}
                     </HealthcareLocationTimetableLoader>
                  )}
               </StyledCalendarPanel>
            )}
         </CalendarController>
      </CalendarActionBar>
   );
};
